/**
 * Household card (design §8): children as "<age> (<DOB>)", vehicles by
 * registration number (legacy rows fall back to plate / make+model), pets by
 * weight ('-' when not collected — the type reads from the icon + suffix,
 * never from the weight slot) — each with its tinted per-type line icon
 * inside the field box. All data is the typed property step; gaps degrade
 * per entry.
 */
import { Baby, Briefcase, BriefcaseBusiness } from 'lucide-react'
import { HousePlus } from 'lucide-react'
import { Fragment, type ReactNode } from 'react'
import {
	childAgeLabel,
	formatPetWeight,
	petTypeLabel,
	vehicleTypeLabel,
	vehicleValueLabel,
} from '#app/utils/application-detail-v2/display'
import { linkedOccupantLabels } from '#app/utils/application-detail-v2/student-housing-display'
import type { ParsedStepData } from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { formatPhoneNumberWithFallback } from '#app/utils/phone-formatting'
import { petTypeIcon, vehicleTypeIcon } from '../household-icons'
import { FieldBox, SectionCard } from '../primitives'

function EntryValue({
	icon,
	iconClass,
	value,
	suffix,
}: {
	icon: ReactNode
	iconClass: string
	value: string
	suffix?: string | null
}) {
	return (
		<span className="flex items-center gap-2 text-sm text-[#32302F]">
			<span className={`shrink-0 [&>svg]:size-4 ${iconClass}`}>{icon}</span>
			<span className="font-semibold">{value}</span>
			{suffix ? (
				<span className="text-xs text-[#747474]">({suffix})</span>
			) : null}
		</span>
	)
}

export function Household({
	propertyStep,
}: {
	propertyStep: ParsedStepData['property']
}) {
	const { formatShortDate } = useFormatDate()

	const isStudentHousing = propertyStep?.isStudentHousing === true
	if (isStudentHousing) {
		const adults = (propertyStep?.adults ?? []) as Array<
			Record<string, unknown>
		>
		const guarantors = (propertyStep?.guarantors ?? []) as Array<
			Record<string, unknown>
		>

		return (
			<SectionCard
				tone="green"
				icon={<BriefcaseBusiness />}
				title="Guarantor Details"
				columns={2}
			>
				{guarantors.length === 0 ? (
					<p className="py-3 text-sm text-[#747474] xl:col-span-2">
						No guarantors listed on this application.
					</p>
				) : (
					guarantors.map((g, gi) => {
						const linked = linkedOccupantLabels(
							Array.isArray(g.linkedOccupantIds)
								? (g.linkedOccupantIds as string[])
								: [],
							adults,
						)
						const key =
							typeof g.id === 'string' && g.id.trim() ? g.id : `g-${gi}`
						return (
							<Fragment key={key}>
								{guarantors.length > 1 ? (
									<p className="text-[15px] font-semibold text-[#32302F] xl:col-span-2">
										Guarantor {gi + 1}
									</p>
								) : null}
								<FieldBox
									label="First Name"
									value={
										typeof g.firstName === 'string' ? g.firstName : undefined
									}
								/>
								<FieldBox
									label="Last Name"
									value={
										typeof g.lastName === 'string' ? g.lastName : undefined
									}
								/>
								<FieldBox
									label="Email"
									value={typeof g.email === 'string' ? g.email : undefined}
								/>
								<FieldBox
									label="Phone"
									value={formatPhoneNumberWithFallback(
										g.phone,
										g.phoneCountryCode,
									)}
								/>
								<FieldBox
									label="Relationship"
									value={
										typeof g.relationship === 'string'
											? g.relationship
											: undefined
									}
								/>
								<FieldBox label="Linked To" value={linked} />
							</Fragment>
						)
					})
				)}
			</SectionCard>
		)
	}

	const children = propertyStep?.children ?? []
	const vehicles = propertyStep?.vehicles ?? []
	const pets = propertyStep?.pets ?? []
	const isEmpty =
		children.length === 0 && vehicles.length === 0 && pets.length === 0
	// Design §8 labels entries 'Child 1'/'Pet 2' but a lone vehicle plain
	// 'Vehicle' — number only when there are several of a kind.
	const entryLabel = (base: string, index: number, total: number) =>
		total > 1 ? `${base} ${index + 1}` : base

	return (
		<SectionCard tone="rose" icon={<HousePlus />} title="Households">
			{isEmpty ? (
				<p className="py-3 text-sm text-[#747474] sm:col-span-2">
					No children, pets, or vehicles reported.
				</p>
			) : (
				<>
					{children.map((child, index) => {
						const age = childAgeLabel(child.dateOfBirth, new Date())
						return (
							<FieldBox
								key={`child-${index}`}
								label={entryLabel('Child', index, children.length)}
							>
								<EntryValue
									icon={<Baby />}
									iconClass="text-[#A5BB83]"
									value={age ?? child.name ?? '-'}
									suffix={
										child.dateOfBirth
											? formatShortDate(child.dateOfBirth)
											: null
									}
								/>
							</FieldBox>
						)
					})}
					{vehicles.map((vehicle, index) => (
						<FieldBox
							key={`vehicle-${index}`}
							label={entryLabel('Vehicle', index, vehicles.length)}
						>
							<EntryValue
								icon={vehicleTypeIcon(vehicle.vehicleType)}
								iconClass="text-[#9976B8]"
								value={vehicleValueLabel(vehicle) ?? '-'}
								suffix={vehicleTypeLabel(vehicle.vehicleType)}
							/>
						</FieldBox>
					))}
					{pets.map((pet, index) => (
						<FieldBox
							key={`pet-${index}`}
							label={entryLabel('Pet', index, pets.length)}
						>
							<EntryValue
								icon={petTypeIcon(pet.petType)}
								iconClass="text-[#BE7B97]"
								value={formatPetWeight(pet.weight) ?? '-'}
								suffix={petTypeLabel(pet.petType) ?? pet.breed}
							/>
						</FieldBox>
					))}
				</>
			)}
		</SectionCard>
	)
}
